import React, { useState, useEffect } from 'react';
import { useMaintenance } from '../contexts/MaintenanceContext';
import {
  X, Check, Sparkles, Scissors, Droplets, Eye, Heart, Briefcase,
  Calendar, Trash2, AlertCircle,
} from 'lucide-react';

const CATEGORIES = [
  { id: 'nails', label: 'Nails', icon: <Sparkles size={14} />, defaultInterval: 14, defaultTitle: 'Nail routine' },
  { id: 'hair', label: 'Hair', icon: <Scissors size={14} />, defaultInterval: 42, defaultTitle: 'Hair routine' },
  { id: 'lashes', label: 'Lashes', icon: <Eye size={14} />, defaultInterval: 21, defaultTitle: 'Lash routine' },
  { id: 'skin', label: 'Skin', icon: <Droplets size={14} />, defaultInterval: 30, defaultTitle: 'Skin routine' },
  { id: 'spa', label: 'Spa', icon: <Heart size={14} />, defaultInterval: 30, defaultTitle: 'Spa routine' },
  { id: 'makeup', label: 'Makeup', icon: <Briefcase size={14} />, defaultInterval: 30, defaultTitle: 'Makeup routine' },
];

const INTERVAL_PRESETS = [
  { label: '1w', fullLabel: '1 week', days: 7 },
  { label: '2w', fullLabel: '2 weeks', days: 14 },
  { label: '3w', fullLabel: '3 weeks', days: 21 },
  { label: '1m', fullLabel: '1 month', days: 30 },
  { label: '6w', fullLabel: '6 weeks', days: 42 },
  { label: '2m', fullLabel: '2 months', days: 60 },
];

const MaintenanceModal = ({ schedule, onClose, onSuccess }) => {
  const { addSchedule, updateSchedule, deleteSchedule } = useMaintenance();
  const isEditing = !!schedule;

  const [formData, setFormData] = useState({
    category: 'nails',
    title: 'Nail routine',
    intervalDays: 14,
    lastServiceDate: new Date().toISOString().split('T')[0],
    price: 0,
    notes: '',
    reminderEnabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (schedule) {
      setFormData({
        category: schedule.category || 'nails',
        title: schedule.title || 'Routine',
        intervalDays: schedule.intervalDays || 14,
        lastServiceDate: schedule.lastServiceDate || new Date().toISOString().split('T')[0],
        price: schedule.price || 0,
        notes: schedule.notes || '',
        reminderEnabled: schedule.reminderEnabled !== false,
      });
    }
  }, [schedule]);

  const handleCategoryPick = (catId) => {
    const cat = CATEGORIES.find(c => c.id === catId);
    setFormData(prev => ({
      ...prev,
      category: catId,
      title: prev.title && prev.title !== 'Routine' ? prev.title : (cat?.defaultTitle || 'Routine'),
      intervalDays: cat?.defaultInterval || prev.intervalDays,
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError(null);

    if (!formData.intervalDays || formData.intervalDays < 1) {
      setError('Interval must be at least 1 day');
      return;
    }
    if (!formData.lastServiceDate) {
      setError('Please select the last service date');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await updateSchedule(schedule.id, formData);
      } else {
        await addSchedule(formData);
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save routine');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing) return;
    if (!window.confirm('Remove this routine?')) return;
    setDeleting(true);
    try {
      await deleteSchedule(schedule.id);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError('Failed to delete');
      setDeleting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[85vh] sm:max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — compact */}
        <div className="px-4 py-3 sm:p-5 border-b border-gray-50 flex items-center justify-between shrink-0">
          <div>
            <h2 className="font-serif font-bold text-base sm:text-xl text-gray-900">
              {isEditing ? 'Edit Routine' : 'Add Routine'}
            </h2>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 hidden sm:block">
              {isEditing ? 'Update your maintenance reminder' : 'Track a recurring beauty routine'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — tighter spacing + smaller inputs on mobile */}
        <div className="px-4 py-3 sm:p-5 space-y-3.5 sm:space-y-5 overflow-y-auto flex-1 min-h-0">

          {/* Category — 6 across on mobile, 3 on desktop */}
          <div>
            <label className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
              What routine?
            </label>
            <div className="grid grid-cols-6 sm:grid-cols-3 gap-1.5 sm:gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCategoryPick(cat.id)}
                  className={`py-2 sm:p-3 rounded-xl sm:rounded-2xl border transition-colors flex flex-col items-center gap-0.5 sm:gap-1.5 ${
                    formData.category === cat.id
                      ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                      : 'border-gray-100 hover:border-[#FBE1E8]'
                  }`}
                >
                  <div className={formData.category === cat.id ? 'text-[#EFA9B6]' : 'text-gray-400'}>
                    {cat.icon}
                  </div>
                  <span className={`text-[9px] sm:text-[11px] font-bold ${
                    formData.category === cat.id ? 'text-[#EFA9B6]' : 'text-gray-500'
                  }`}>
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1 sm:mb-1.5">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Gel manicure"
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-3 text-sm focus:outline-none focus:border-[#EFA9B6] focus:bg-white transition-colors"
            />
          </div>

          <div>
            <label className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
              How often?
            </label>
            <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-1.5 sm:mb-2">
              {INTERVAL_PRESETS.map(preset => (
                <button
                  key={preset.days}
                  type="button"
                  onClick={() => setFormData({ ...formData, intervalDays: preset.days })}
                  className={`px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-full text-[10px] sm:text-[11px] font-bold transition-colors border ${
                    formData.intervalDays === preset.days
                      ? 'bg-[#010101] text-white border-[#010101]'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-[#EFA9B6]'
                  }`}
                  title={preset.fullLabel}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-[11px] text-gray-500">or</span>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.intervalDays}
                onChange={(e) => setFormData({ ...formData, intervalDays: parseInt(e.target.value) || 0 })}
                className="w-16 sm:w-20 bg-[#FCF8F9] border border-transparent rounded-lg px-2.5 py-1 sm:px-3 sm:py-1.5 text-sm focus:outline-none focus:border-[#EFA9B6]"
              />
              <span className="text-[11px] text-gray-500">days</span>
            </div>
          </div>

          <div>
            <label className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1 sm:mb-1.5 flex items-center gap-1 sm:gap-1.5">
              <Calendar size={10} />
              When was your last {formData.category}?
            </label>
            <input
              type="date"
              value={formData.lastServiceDate}
              max={today}
              onChange={(e) => setFormData({ ...formData, lastServiceDate: e.target.value })}
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-3 text-sm focus:outline-none focus:border-[#EFA9B6] focus:bg-white transition-colors"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1 sm:mb-1.5">
                Price (R)
              </label>
              <input
                type="number"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-3 text-sm focus:outline-none focus:border-[#EFA9B6] focus:bg-white transition-colors"
              />
            </div>
            <div>
              <label className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1 sm:mb-1.5">
                Reminder
              </label>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, reminderEnabled: !formData.reminderEnabled })}
                className={`w-full h-[42px] sm:h-[46px] rounded-xl flex items-center justify-center gap-2 text-xs font-bold transition-colors ${
                  formData.reminderEnabled
                    ? 'bg-[#FFF5F6] text-[#EFA9B6] border border-[#FBE1E8]'
                    : 'bg-[#FCF8F9] text-gray-400 border border-transparent'
                }`}
              >
                {formData.reminderEnabled ? 'On' : 'Off'}
              </button>
            </div>
          </div>

          <div>
            <label className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1 sm:mb-1.5">
              Notes (optional)
            </label>
            <textarea
              rows="2"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any special notes..."
              className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-3 text-sm focus:outline-none focus:border-[#EFA9B6] focus:bg-white transition-colors resize-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-2.5 sm:p-3 flex items-start gap-2">
              <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-[11px] sm:text-xs text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer — compact */}
        <div className="px-4 py-3 sm:p-5 pt-3 sm:pt-4 border-t border-gray-50 shrink-0 space-y-2">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="w-full bg-[#010101] text-white py-3 sm:py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <Check size={16} /> {isEditing ? 'Save Changes' : 'Add Routine'}
              </>
            )}
          </button>

          {isEditing && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="w-full py-2.5 sm:py-3 rounded-full bg-red-50 text-red-500 font-bold text-xs sm:text-sm hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {deleting ? (
                <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Trash2 size={13} /> Remove Routine
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceModal;